import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import CuiLitePanel from './CuiLitePanel';
import { validateApiResponse } from '../../lib/validateApiResponse';

/**
 * Chat im Sales-Bereich — die KI-Unterstuetzung eines Sales-Partners, in seinem
 * EIGENEN Arbeitsordner.
 *
 * Der Chat gab es bisher nur im Arbeitsbereich, und dort laeuft er in einem
 * geteilten Workspace: Markus landete in `werking-energy` und sah damit die
 * Konversationen von David Steiner und Kurt Engelmann. Deshalb faehrt dieser
 * Chat ausschliesslich in dem Ordner, der laut users.json diesem Nutzer gehoert
 * (`personalWorkspace`) — dort liegen seine Vertraege und Unterlagen, und sonst
 * nichts.
 *
 * Welcher Ordner das ist, sagt der Server (GET /api/partner/sales/area). Ihn im
 * Client zu raten waere die Art Fehler, die niemandem auffaellt und trotzdem
 * fremde Inhalte oeffnet.
 */

const API = '/api/partner';

interface SalesArea {
  workspace: { projectId: string; name: string; workDir: string } | null;
  workspaceError?: string;
}

export default function SalesChatPanel() {
  const { user } = useAuth();
  const [area, setArea] = useState<SalesArea | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API}/sales/area`, { signal: AbortSignal.timeout(15000) });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const raw = await res.json();
      const data = validateApiResponse<{ workspace: unknown; workspaceError?: string }>(
        raw, '/api/partner/sales/area', {
          workspace: { type: 'object', optional: true },
          workspaceError: { type: 'string', optional: true },
        },
      );
      const ws = data.workspace
        ? validateApiResponse<{ projectId: string; name: string; workDir: string }>(
            data.workspace, '/api/partner/sales/area.workspace', {
              projectId: 'string', name: 'string', workDir: 'string',
            },
          )
        : null;
      setArea({ workspace: ws, workspaceError: data.workspaceError });
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ padding: 20, color: 'var(--tn-text-muted)', fontSize: 13 }}>Lädt…</div>;
  }

  if (error || !area?.workspace) {
    const meldung = error || area?.workspaceError || 'Kein Arbeitsordner hinterlegt.';
    return (
      <div style={{ padding: 24, maxWidth: 620 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--tn-text)', marginBottom: 8 }}>
          Chat steht noch nicht bereit
        </div>
        <div style={{ fontSize: 13, color: 'var(--tn-text-muted)', lineHeight: 1.6, marginBottom: 14 }}>
          {meldung}
          <br />
          Schreib Rafael kurz über den Reiter „Nachrichten" — er richtet das ein.
        </div>
        <button
          data-ai-id="sales-chat-retry"
          onClick={() => { setLoading(true); load(); }}
          style={{
            padding: '6px 14px', borderRadius: 5, fontSize: 13, cursor: 'pointer',
            background: 'var(--tn-surface)', color: 'var(--tn-text)', border: '1px solid var(--tn-border)',
          }}
        >
          Erneut versuchen
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{
        padding: '8px 16px', borderBottom: '1px solid var(--tn-border)', flexShrink: 0,
        fontSize: 12, color: 'var(--tn-text-muted)',
      }}>
        Deine KI-Unterstützung — sie kennt deine Unterlagen in „{area.workspace.name}".
        Frag nach Preisen, Vertragsklauseln, Formulierungen für ein Angebot oder lass dir ein
        Kundengespräch vorbereiten. Über „☰ Meine Chats" kommst du an frühere Gespräche
        und startest ein neues.
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <CuiLitePanel
          accountId={user?.claudeAccountId}
          projectId={area.workspace.projectId}
          workDir={area.workspace.workDir}
          panelId="sales-chat"
          simpleMode
        />
      </div>
    </div>
  );
}
